import { useState, useEffect } from 'react';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import Button from '../ui/Button';
import {
  Clock, Plus, Edit, Trash2, CheckCircle, XCircle,
  AlertCircle, FileText, RefreshCw, Loader2
} from 'lucide-react';
import type { AuditLogEntry, Pagination } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';

interface AuditTimelineProps {
  entityType: string;
  entityId: number;
}

const ACTION_CONFIG: Record<string, {
  icon: typeof Plus;
  iconColor: string;
  bgColor: string;
  label: string;
}> = {
  create: {
    icon: Plus,
    iconColor: 'text-success-600',
    bgColor: 'bg-success-100',
    label: 'Created',
  },
  update: {
    icon: Edit,
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-100',
    label: 'Updated',
  },
  delete: {
    icon: Trash2,
    iconColor: 'text-error-600',
    bgColor: 'bg-error-100',
    label: 'Deleted',
  },
  status_change: {
    icon: RefreshCw,
    iconColor: 'text-warning-600',
    bgColor: 'bg-warning-100',
    label: 'Status Changed',
  },
  confirm: {
    icon: CheckCircle,
    iconColor: 'text-success-600',
    bgColor: 'bg-success-100',
    label: 'Confirmed',
  },
  acknowledge: {
    icon: CheckCircle,
    iconColor: 'text-success-600',
    bgColor: 'bg-success-100',
    label: 'Acknowledged',
  },
  revoke: {
    icon: XCircle,
    iconColor: 'text-error-600',
    bgColor: 'bg-error-100',
    label: 'Revoked',
  },
  query: {
    icon: AlertCircle,
    iconColor: 'text-warning-600',
    bgColor: 'bg-warning-100',
    label: 'Queried',
  },
};

function AuditTimeline({ entityType, entityId }: AuditTimelineProps) {
  const timezone = useOrgTimezone();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadAuditHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const loadAuditHistory = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const response = await cultivarService.getAuditHistory(entityType, entityId, page, 20);
      if (response.success && response.data) {
        if (page === 1) {
          setEntries(response.data.entries);
        } else {
          setEntries(prev => [...prev, ...response.data!.entries]);
        }
        setPagination(response.data.pagination);
      } else {
        setError(response.message || 'Failed to load audit history');
      }
    } catch (err) {
      console.error('Error loading audit history:', err);
      setError('Failed to load audit history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => formatDateTimeInTz(dateString, timezone);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const toggleExpanded = (entryId: number) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const renderChanges = (changes: Record<string, unknown>) => {
    const entries = Object.entries(changes);
    if (entries.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        {entries.map(([key, value]) => (
          <div key={key} className="text-sm">
            <span className="font-medium text-neutral-700 capitalize">
              {key.replace(/_/g, ' ')}:
            </span>
            <pre className="mt-1 p-2 bg-neutral-100 rounded text-xs overflow-x-auto">
              {typeof value === 'object'
                ? JSON.stringify(value, null, 2)
                : String(value)}
            </pre>
          </div>
        ))}
      </div>
    );
  };

  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error && entries.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-10 w-10 text-error-400 mx-auto mb-3" />
        <p className="text-neutral-600">{error}</p>
        <Button variant="outline" size="sm" onClick={() => loadAuditHistory()} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
        <p className="text-neutral-500">No audit history available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-neutral-200" />

        {/* Entries */}
        <div className="space-y-4">
          {entries.map((entry) => {
            const config = ACTION_CONFIG[entry.action] || {
              icon: FileText,
              iconColor: 'text-neutral-600',
              bgColor: 'bg-neutral-100',
              label: entry.action,
            };
            const Icon = config.icon;
            const isExpanded = expandedEntries.has(entry.id);

            return (
              <div key={entry.id} className="relative flex gap-4">
                {/* Icon */}
                <div className={`
                  relative z-10 flex-shrink-0 h-8 w-8 rounded-full
                  flex items-center justify-center
                  ${config.bgColor}
                `}>
                  <Icon className={`h-4 w-4 ${config.iconColor}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-4">
                  <div className="bg-white rounded-lg border border-neutral-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-neutral-900">
                          {config.label}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-sm text-neutral-500">
                          <Clock className="h-3.5 w-3.5" />
                          <span title={formatDate(entry.created_at)}>
                            {formatRelativeTime(entry.created_at)}
                          </span>
                          {entry.changed_by_org_name && (
                            <>
                              <span>•</span>
                              <span>{entry.changed_by_org_name}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {entry.changes && Object.keys(entry.changes).length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(entry.id)}
                        >
                          {isExpanded ? 'Hide Details' : 'View Details'}
                        </Button>
                      )}
                    </div>

                    {/* Expanded Changes */}
                    {isExpanded && entry.changes && renderChanges(entry.changes)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Load More */}
      {pagination && pagination.page < pagination.pages && (
        <div className="text-center pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadAuditHistory(pagination.page + 1)}
            isLoading={loading}
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}

export default AuditTimeline;
